import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, RequestError } from "../api/client";
import type { Account, Reference, Replay } from "../api/types";
import { AccountMenu } from "../components/AccountMenu";
import { SignIn } from "../components/SignIn";
import { useMe } from "../lib/account";
import { expiresIn, formatBytes, formatDate, formatDuration } from "../lib/view";

const STATUS_LABEL: Record<string, string> = {
  pending: "en attente",
  processing: "décodage",
  ready: "prêt",
  unsupported: "version non prise en charge",
  failed: "échec",
  expired: "lien expiré",
};

/** The home page: the signed-in account's replays, or the way to sign in. */
export function Library() {
  const me = useMe();
  const [search] = useSearchParams();
  const account = me.data?.account ?? null;

  return (
    <main className="mx-auto max-w-5xl px-6 py-10">
      <header className="mb-8 flex items-start gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Analyse de replays Valorant</h1>
          <p className="mt-1 text-sm opacity-60">
            Déposez un fichier .vrf pour rejouer la partie sur la carte.
          </p>
        </div>
        {account && (
          <div className="ml-auto">
            <AccountMenu account={account} />
          </div>
        )}
      </header>

      {me.isLoading && <p className="text-sm opacity-60">Chargement…</p>}
      {me.error && <p className="text-sm text-red-400">Le serveur ne répond pas.</p>}
      {me.data && !account && (
        <SignIn available={me.data.discord} failure={search.get("auth")} />
      )}
      {account && <Shelf account={account} />}
    </main>
  );
}

/** Everything one account has uploaded, and the place to upload more. */
function Shelf({ account }: { account: Account }) {
  const queryClient = useQueryClient();
  // Keyed by account: what one account listed is never shown to the next.
  const replays = useQuery({ queryKey: ["replays", account.id], queryFn: api.listReplays });
  const reference = useQuery({ queryKey: ["reference"], queryFn: api.reference, staleTime: Infinity });

  // Follow anything still decoding, so the list settles on its own.
  useEffect(() => {
    const stops = (replays.data ?? [])
      .filter((r) => r.status === "pending" || r.status === "processing")
      .map((r) =>
        api.watch(r.id, (updated) => {
          queryClient.setQueryData<Replay[]>(["replays", account.id], (old) =>
            (old ?? []).map((item) => (item.id === updated.id ? updated : item)),
          );
        }),
      );
    return () => stops.forEach((stop) => stop());
  }, [replays.data, queryClient, account.id]);

  return (
    <>
      <UploadZone onDone={() => queryClient.invalidateQueries({ queryKey: ["replays"] })} />

      <Simulation />

      <section className="mt-8">
        {replays.isLoading && <p className="text-sm opacity-60">Chargement…</p>}
        {replays.error && (
          <p className="text-sm text-red-400">Impossible de charger la bibliothèque.</p>
        )}
        {replays.data?.length === 0 && (
          <p className="text-sm opacity-60">Aucun replay pour l'instant.</p>
        )}
        <ul className="space-y-2">
          {replays.data?.map((replay) => (
            <ReplayRow
              key={replay.id}
              replay={replay}
              reference={reference.data}
              onDelete={async () => {
                await api.deleteReplay(replay.id);
                queryClient.invalidateQueries({ queryKey: ["replays"] });
              }}
            />
          ))}
        </ul>
      </section>
    </>
  );
}

/** Builds and opens the simulated match. It exists to check the viewer against
 *  agents no real replay happens to contain, and only appears in debug mode. */
function Simulation() {
  const navigate = useNavigate();
  const health = useQuery({ queryKey: ["health"], queryFn: api.health, staleTime: Infinity });
  const [error, setError] = useState<string | null>(null);

  const build = useMutation({
    mutationFn: api.buildSimulation,
    onMutate: () => setError(null),
    onError: (err) => setError(err instanceof RequestError ? err.message : "échec"),
    onSuccess: (replay) => navigate(`/replay/${replay.id}`),
  });

  if (!health.data?.debug) return null;

  return (
    <div className="mt-3 flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3">
      <span className="text-sm opacity-70">
        Partie de test : un round par agent, toutes ses compétences posées côte à côte.
      </span>
      <button
        onClick={() => build.mutate()}
        disabled={build.isPending}
        className="ml-auto shrink-0 rounded bg-white/10 px-3 py-1.5 text-sm hover:bg-white/20 disabled:opacity-50"
      >
        {build.isPending ? "Génération…" : "Ouvrir"}
      </button>
      {error && <span className="text-sm text-red-400">{error}</span>}
    </div>
  );
}

function ReplayRow({
  replay,
  reference,
  onDelete,
}: {
  replay: Replay;
  reference: Reference | undefined;
  onDelete: () => void;
}) {
  const map = reference?.maps.find((m) => m.mapUrl === replay.mapUrl);
  const playable = replay.status === "ready";
  const busy = replay.status === "pending" || replay.status === "processing";

  const body = (
    <div
      className={`flex items-center gap-4 rounded-lg border border-white/10 bg-white/[0.03] p-3 ${
        playable ? "hover:border-white/25" : ""
      }`}
    >
      {map?.minimap ? (
        <img src={map.minimap} alt="" className="h-14 w-14 rounded object-cover opacity-80" />
      ) : (
        <div className="h-14 w-14 rounded bg-white/5" />
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="font-medium">{map?.displayName ?? replay.mapUrl ?? "Carte inconnue"}</span>
          <span className="text-xs opacity-50">{replay.build}</span>
        </div>
        <div className="mt-0.5 text-xs opacity-60">
          {formatDate(replay.recordedAt ?? replay.createdAt)} · {replay.roundCount} rounds ·{" "}
          {formatDuration(replay.durationMs)} · {formatBytes(replay.sizeBytes)}
        </div>
        {busy && (
          <div className="mt-2 h-1 w-48 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full bg-white/60 transition-all"
              style={{ width: `${Math.max(5, replay.progress)}%` }}
            />
          </div>
        )}
        {replay.errorMessage && (
          <p className="mt-1 text-xs text-amber-400">{replay.errorMessage}</p>
        )}
      </div>

      <div className="shrink-0 text-right text-xs">
        <div className={playable ? "text-emerald-400" : "opacity-60"}>
          {STATUS_LABEL[replay.status] ?? replay.status}
        </div>
        {replay.status !== "expired" && (
          <div className="mt-1 opacity-40">expire dans {expiresIn(replay.expiresAt)}</div>
        )}
      </div>

      <button
        onClick={(e) => {
          e.preventDefault();
          onDelete();
        }}
        title="Supprimer"
        className="shrink-0 rounded px-2 py-1 text-sm opacity-40 hover:bg-white/10 hover:opacity-100"
      >
        ✕
      </button>
    </div>
  );

  return <li>{playable ? <Link to={`/replay/${replay.id}`}>{body}</Link> : body}</li>;
}

function UploadZone({ onDone }: { onDone: () => void }) {
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: (file: File) => api.upload(file, setProgress),
    onMutate: () => {
      setError(null);
      setProgress(0);
    },
    onError: (err) => {
      setError(err instanceof RequestError ? err.message : "envoi impossible");
      setProgress(null);
    },
    onSuccess: () => {
      setProgress(null);
      onDone();
    },
  });

  const send = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".vrf")) {
      setError("ce fichier n'est pas un replay .vrf");
      return;
    }
    upload.mutate(file);
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        send(e.dataTransfer.files);
      }}
      onClick={() => inputRef.current?.click()}
      className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition ${
        dragging ? "border-white/50 bg-white/5" : "border-white/15 hover:border-white/30"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".vrf"
        hidden
        onChange={(e) => send(e.target.files)}
      />
      {progress === null ? (
        <>
          <p className="font-medium">Déposez un replay .vrf</p>
          <p className="mt-1 text-xs opacity-50">
            Vos replays se trouvent dans %LOCALAPPDATA%\VALORANT\Saved\Demos
          </p>
        </>
      ) : (
        <>
          <p className="font-medium">Envoi… {Math.round(progress * 100)} %</p>
          <div className="mx-auto mt-3 h-1 w-64 overflow-hidden rounded-full bg-white/10">
            <div className="h-full bg-white/70" style={{ width: `${progress * 100}%` }} />
          </div>
        </>
      )}
      {error && <p className="mt-3 text-sm text-red-400">{error}</p>}
    </div>
  );
}
